import React, { useCallback, useMemo, useState, useContext, useEffect } from 'react'
import {
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  useTheme,
  OutlinedInput,
  Tooltip,
  Switch,
  Checkbox,
  CircularProgress,
} from '@mui/material'
import { SnackbarHelperContext } from '../../contexts/SnackbarHelperContext'
import setNestedValue from 'lodash.set'
import InfoIcon from '@mui/icons-material/Info'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import CheckIcon from '@mui/icons-material/Check'
import TuneIcon from '@mui/icons-material/Tune'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_firestore, firebase_functions } from '../../common/firebaseConfig'
import {
  Alert,
  AtrOptions,
  FixedOptions,
  HighLowOptions,
  IncrementalOptions,
  PeakValleyOptions,
  useApplicationSuiteContext,
} from '../../contexts/ApplicationSuiteProvider'
import { parseFloatOr, parseIntOr } from '../../common/utils/parseNumericInput'
import ExitStrategyInputs from '../../app/backtest/components/exitStrategies/ExitStrategyInputs'
import StopLossStrategySelect from '../../app/backtest/components/exitStrategies/StopLossStrategySelect'
import ForceExitField from '../../layout/settingsMenu/sections/AccountSettingsEdit/fields/ForceExitField'
import { collection, deleteDoc, doc, setDoc, updateDoc } from 'firebase/firestore'
import MultiSlideDialog, { SlideStep } from '../common/MultiSlideDialog'
import MetaTraderAccountForm, { MetaTraderAccount } from '../forms/MetaTraderAccountForm'
import PatternSelect from '../form/PatternSelect'
import { Pattern } from '../../app/builder/contexts/PatternContext'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { setNestedAlertSymbol } from '../../common/utils/setNestedAlertSymbol'

type BrokerUserInputs = {
  symbol?: string
}

type TradingOptions = NonNullable<Alert['trading']>['options']

const defaultATRExitStrategy: AtrOptions = {
  exitStrategy: 'atr',
  atrChartPeriod: 5,
  atrChartTimeframe: 'minute',
  atrPeriod: 15,
}

const defaultIncrementalExitStrategy: IncrementalOptions = {
  exitStrategy: 'incremental',
  incrementalStart: 0.0005,
  incrementalStop: 0.0001,
  incrementalStep: 0.0001,
}

const defaultPeakValleyExitStrategy: PeakValleyOptions = {
  exitStrategy: 'peak_valley',
  fractalCount: 2,
  maxPeakValleyLookback: 10,
  priceOffset: 0.0001,
}

const defaultHighLowExitStrategy: HighLowOptions = {
  exitStrategy: 'high_low',
  maxLookback: 10,
  priceOffset: 0.0001,
}

const defaultFixedExitStrategy: FixedOptions = {
  exitStrategy: 'fixed',
}

function TradingAccountDialog({
  open,
  onClose,
  alert,
}: {
  open: boolean
  onClose: () => void
  alert: Alert
}) {
  const { usermeta } = useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [account, setAccount] = useState<MetaTraderAccount>({
    login: '',
    password: '',
    server: '',
    platform: 'mt4',
    nickname: '',
  })
  const [selectedAccountId, setSelectedAccountId] = useState<string>(() => {
    // If alert has an account ID, use it
    if (alert.trading?.accountId) {
      return alert.trading.accountId
    }
    // Otherwise, auto-select the first available account
    const availableAccounts = usermeta.metaapi?.accounts || []
    return availableAccounts.length > 0 ? availableAccounts[0].accountId : ''
  })
  const [showCreateAccountForm, setShowCreateAccountForm] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isUpdatingAlertTradingOptions, setIsUpdatingAlertTradingOptions] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [demoAccountConfirmed, setDemoAccountConfirmed] = useState<boolean>(false)
  const [brokerUserInputs, setBrokerUserInputs] = useState<BrokerUserInputs>()
  const [brokerSettings, setBrokerSettings] = useState<any>(null)
  const [isFetchingBrokerSettings, setIsFetchingBrokerSettings] = useState(false)
  const [exitTradingPlan, setExitTradingPlan] = useState<Alert | null>(null)
  const [exitPatternMode, setExitPatternMode] = useState<
    'both' | 'take_profit_only' | 'stop_loss_only'
  >((alert.trading as any)?.exitPatternMode || 'both')
  const [tradingOptions, setTradingOptions] = useState<TradingOptions>(() => {
    // If editing existing alert, prefill with existing options
    if (alert.trading?.options) {
      return alert.trading.options
    }
    // Otherwise use defaults
    return {
      generateTrades: {
        ...defaultATRExitStrategy,
        ...defaultIncrementalExitStrategy,
        ...defaultPeakValleyExitStrategy,
        ...defaultFixedExitStrategy,
        ...defaultHighLowExitStrategy,
        exitStrategy: 'atr',
        tradeRatios: [],
      },
      analyzeTrades: {
        riskPerTrade: 0.01,
        tradeLimit: 0,
        dailyTradeLimit: 0,
        allowHedging: false,
      },
    }
  })

  const exitTradingPlanDocRef = useMemo(() => {
    const id = alert.trading?.exitTradingPlanId
    return id ? doc(firebase_firestore, 'tradingPlans', id) : null
  }, [alert.trading?.exitTradingPlanId])

  const [existingExitTradingPlan, existingExitTradingPlanLoading] = useDocumentData(
    exitTradingPlanDocRef as any
  )
  const [getBrokerSettingsFunc] = useHttpsCallable(firebase_functions, 'getBrokerSettings')
  const [createMetaApiAccountFunc, createMetaApiAccountLoading] = useHttpsCallable(
    firebase_functions,
    'createMetaApiAccount'
  )

  const isEditing = Boolean(alert.trading?.options)
  const demoOnly = useMemo(
    () => alert.id === usermeta.tradingHouses.gtf?.tpaAffiliateAlertId,
    [alert.id, usermeta.tradingHouses.gtf?.tpaAffiliateAlertId]
  )

  // Validation for trade ratios
  const isTradeRatiosValid = useMemo(() => {
    return (
      tradingOptions.generateTrades.tradeRatios &&
      Array.isArray(tradingOptions.generateTrades.tradeRatios) &&
      tradingOptions.generateTrades.tradeRatios.length > 0
    )
  }, [tradingOptions.generateTrades.tradeRatios])

  const isBrokerSettingsValid = useMemo(() => {
    if (brokerSettings?.required) {
      for (const key of Object.keys(brokerSettings.required)) {
        if (
          brokerSettings.required[key] &&
          (!brokerUserInputs ||
            brokerUserInputs[key as keyof BrokerUserInputs] == null ||
            brokerUserInputs[key as keyof BrokerUserInputs] === '')
        ) {
          return false
        }
      }
    }
    return true
  }, [brokerSettings, brokerUserInputs])

  // Form errors for validation
  const formErrors = useMemo(() => {
    const errors: any = {}
    if (!isTradeRatiosValid) {
      errors.generateTrades = {
        tradeRatios: 'Risk/Reward ratios are required',
      }
    }
    if (
      brokerSettings?.required?.symbol &&
      (brokerUserInputs?.symbol == null || brokerUserInputs?.symbol === '')
    ) {
      errors.broker = {
        symbol: `${alert.symbol} is not supported by this broker`,
      }
    }
    return errors
  }, [isTradeRatiosValid, brokerSettings, brokerUserInputs, alert.symbol])

  useEffect(() => {
    if (selectedAccountId) {
      setBrokerSettings(null)
      setBrokerUserInputs({})
      setIsFetchingBrokerSettings(true)
      getBrokerSettingsFunc({ accountId: selectedAccountId, alertId: alert.id })
        .then((res: any) => {
          if (res?.data) setBrokerSettings(res.data)
        })
        .finally(() => {
          setIsFetchingBrokerSettings(false)
        })
    }
  }, [selectedAccountId, alert.id, getBrokerSettingsFunc])

  useEffect(() => {
    setBrokerUserInputs(alert.trading?.broker || {})
  }, [alert.trading?.broker])

  useEffect(() => {
    if (!existingExitTradingPlan) {
      setExitTradingPlan(null)
      return
    }

    setExitTradingPlan(existingExitTradingPlan as Alert)
  }, [existingExitTradingPlan, alert.trading?.exitTradingPlanId])

  const handleClose = useCallback(() => {
    setAccount({
      login: '',
      password: '',
      server: '',
      platform: 'mt4',
      nickname: '',
    })
    // Reset to original state: alert account ID or first available account
    const originalAccountId =
      alert.trading?.accountId ||
      (usermeta.metaapi?.accounts?.length > 0 ? usermeta.metaapi.accounts[0].accountId : '')
    setSelectedAccountId(originalAccountId)
    setShowCreateAccountForm(false)
    setTradingOptions({
      generateTrades: {
        ...defaultATRExitStrategy,
        ...defaultIncrementalExitStrategy,
        ...defaultPeakValleyExitStrategy,
        ...defaultFixedExitStrategy,
        exitStrategy: 'atr',
        tradeRatios: [],
      },
      analyzeTrades: {
        riskPerTrade: 0.01,
        tradeLimit: 0,
        dailyTradeLimit: 0,
        allowHedging: false,
      },
    })
    setErrorMessage(null)
    setShowPassword(false)
    setDemoAccountConfirmed(false)
    setExitPatternMode((alert.trading as any)?.exitPatternMode || 'both')
    onClose()
  }, [onClose, alert.trading, usermeta.metaapi?.accounts])

  const handleAccountKeyChange = useCallback((key: string, value: any) => {
    setAccount((prev) => setNestedValue({ ...prev }, key, value))
  }, [])

  const handleTradingOptionChange = useCallback((key: string, value: any) => {
    setTradingOptions((prev) => setNestedValue({ ...prev }, key, value))
  }, [])

  const handleBrokerUserInputChange = useCallback((key: string, value: any) => {
    setBrokerUserInputs((prev: any) => setNestedValue({ ...prev }, key, value))
  }, [])

  const createMetaApiAccount = useCallback(async () => {
    try {
      setErrorMessage(null)
      const response: any = await createMetaApiAccountFunc(account)

      if (!response?.data?.status || response?.data?.status >= 400) {
        setErrorMessage(response?.data?.message || 'Failed to link MetaTrader account')
        setSnackbarSettings({
          severity: 'error',
          message: response?.data?.message || 'Failed to link MetaTrader account',
          autoHideDuration: 6000,
        })
        return false
      }

      // Auto-select the newly created account
      if (response?.data?.accountId) {
        setSelectedAccountId(response.data.accountId)
      }

      setSnackbarSettings({
        severity: 'success',
        message: 'MetaTrader account connected successfully!',
        autoHideDuration: 6000,
      })

      return true
    } catch (error) {
      setErrorMessage('Failed to link MetaTrader account')
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to link MetaTrader account',
        autoHideDuration: 6000,
      })
      return false
    }
  }, [account, createMetaApiAccountFunc, setSnackbarSettings])

  const updateTradingOptions = useCallback(async () => {
    setIsUpdatingAlertTradingOptions(true)
    try {
      setErrorMessage(null)
      if (!alert.id) {
        setErrorMessage('Alert ID not found')
        return false
      }

      if (!selectedAccountId) {
        setErrorMessage('Please select a trading account')
        return false
      }

      // Validate trade ratios before saving
      if (!isTradeRatiosValid) {
        setErrorMessage('Please select at least one risk/reward ratio')
        return false
      }

      if (brokerSettings?.required) {
        for (const key of Object.keys(brokerSettings.required)) {
          if (
            brokerSettings.required[key] &&
            (!brokerUserInputs ||
              brokerUserInputs[key as keyof BrokerUserInputs] == null ||
              brokerUserInputs[key as keyof BrokerUserInputs] === '')
          ) {
            setErrorMessage(`Please configure the "${key}" for this account`)
            return false
          }
        }
      }

      const alertRef = doc(firebase_firestore, 'tradingPlans', alert.id)
      const updateData: any = {
        'trading.accountId': selectedAccountId,
        'trading.options': tradingOptions,
        'trading.exitPatternMode': exitPatternMode,
      }

      if (brokerUserInputs) {
        updateData['trading.broker'] = brokerUserInputs
      }

      if (exitTradingPlan?.pattern) {
        // If the exit plan already exists, update it. Otherwise create it.
        const exitTradingPlanId =
          exitTradingPlan.id || doc(collection(firebase_firestore, 'tradingPlans')).id

        updateData['trading.exitTradingPlanId'] = exitTradingPlanId

        const exitTradingPlanRef = doc(firebase_firestore, 'tradingPlans', exitTradingPlanId)

        // Create a clean "exit trading plan" document:
        // - do NOT copy `trading` settings from the base alert
        const exitPattern = structuredClone(exitTradingPlan.pattern)
        setNestedAlertSymbol(
          exitPattern,
          alert.symbol || alert.pattern?.trigger.chartDetails.symbol || ''
        )

        const exitTradingPlanDoc: Alert = {
          user: alert.user,
          active: false,
          pattern: exitPattern,
          id: exitTradingPlanId,
          symbol: alert.symbol || alert.pattern?.trigger.chartDetails.symbol || '',
          timezone: alert.timezone,
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          timeRanges: [{ startTimeRange: '00:00', endTimeRange: '00:00' }],
          alertTypes: ['exitTradingPlan'],
          name: `${alert.name} - Exit`,
        }

        // If it's a create, do a full write; if it's an update, patch only the fields we manage.
        if (exitTradingPlan.id) {
          await updateDoc(exitTradingPlanRef, exitTradingPlanDoc)
        } else {
          await setDoc(exitTradingPlanRef, exitTradingPlanDoc)
          // Keep local state in sync so subsequent saves update instead of creating another doc
          setExitTradingPlan({ ...exitTradingPlan } as Alert)
        }
      } else {
        if (alert.trading?.exitTradingPlanId) {
          const exitTradingPlanRef = doc(
            firebase_firestore,
            'tradingPlans',
            alert.trading.exitTradingPlanId
          )
          await deleteDoc(exitTradingPlanRef)
        }

        // If user disabled exit pattern, clear the reference on the main alert.
        updateData['trading.exitTradingPlanId'] = null
      }

      await updateDoc(alertRef, updateData)

      setSnackbarSettings({
        severity: 'success',
        message: isEditing
          ? 'Trading account updated successfully!'
          : 'Trading options updated successfully!',
        autoHideDuration: 6000,
      })

      return true
    } catch (error) {
      setErrorMessage('Failed to update trading options')
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to update trading options',
        autoHideDuration: 6000,
      })
      return false
    } finally {
      setIsUpdatingAlertTradingOptions(false)
    }
  }, [
    alert,
    tradingOptions,
    selectedAccountId,
    setSnackbarSettings,
    isEditing,
    isTradeRatiosValid,
    brokerSettings,
    brokerUserInputs,
    exitTradingPlan,
    exitPatternMode,
  ])

  const dynamicMaxWidth = useCallback((currentSlide: number) => {
    // Make trading configuration slide wider
    // return currentSlide === 3 ? 'md' as const : 'sm' as const;
    return 'sm' as const
  }, [])

  const steps: SlideStep[] = useMemo(() => {
    return getSteps(
      account,
      selectedAccountId,
      showCreateAccountForm,
      createMetaApiAccount,
      updateTradingOptions,
      createMetaApiAccountLoading,
      isUpdatingAlertTradingOptions,
      errorMessage,
      isEditing,
      isTradeRatiosValid,
      demoOnly,
      demoAccountConfirmed,
      isBrokerSettingsValid
    )
  }, [
    account,
    selectedAccountId,
    showCreateAccountForm,
    createMetaApiAccount,
    updateTradingOptions,
    createMetaApiAccountLoading,
    isUpdatingAlertTradingOptions,
    errorMessage,
    isEditing,
    isTradeRatiosValid,
    demoOnly,
    demoAccountConfirmed,
    isBrokerSettingsValid,
  ])

  const dialogTitle = isEditing ? 'Update Trading Account' : 'Setup Trading Account'

  return (
    <MultiSlideDialog
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      steps={steps}
      dynamicMaxWidth={dynamicMaxWidth}
      slideProps={{
        account,
        onAccountKeyChange: handleAccountKeyChange,
        showPassword,
        setShowPassword,
        tradingOptions,
        onTradingOptionChange: handleTradingOptionChange,
        selectedAccountId,
        setSelectedAccountId,
        showCreateAccountForm,
        setShowCreateAccountForm,
        availableAccounts: usermeta.metaapi?.accounts || [],
        isEditing,
        formErrors,
        isTradeRatiosValid,
        demoOnly,
        demoAccountConfirmed,
        setDemoAccountConfirmed,
        brokerSettings,
        brokerUserInputs,
        onBrokerUserInputChange: handleBrokerUserInputChange,
        exitTradingPlan,
        setExitTradingPlan,
        exitPatternMode,
        setExitPatternMode,
        symbol: alert.symbol || alert.pattern?.trigger?.chartDetails?.symbol,
      }}
      actionProps={{
        account,
        tradingOptions,
        selectedAccountId,
        showCreateAccountForm,
        isEditing,
        onCreateAccount: createMetaApiAccount,
        onUpdateTradingOptions: updateTradingOptions,
        isCreating: createMetaApiAccountLoading,
        isUpdating: isUpdatingAlertTradingOptions,
        errorMessage,
        isTradeRatiosValid,
        brokerSettings,
        getBrokerSettingsLoading: isFetchingBrokerSettings,
        existingExitTradingPlanLoading,
      }}
    />
  )
}

// Slide Components
function SetupAccountSlide({ isEditing }: { isEditing?: boolean }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <AccountBalanceIcon sx={{ fontSize: '64px' }} color="success" />
      <Typography variant="h4" fontWeight="bold">
        {isEditing ? 'Update Trading Account' : 'Connect MetaTrader'}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {isEditing
          ? 'Update your trading account selection and parameters for this alert.'
          : 'Connect your MetaTrader account to enable automated trading. Your account credentials are securely encrypted and used only to connect to MetaTrader.'}
      </Typography>
    </Box>
  )
}

function AccountSelectionSlide({
  selectedAccountId,
  setSelectedAccountId,
  availableAccounts,
  showCreateAccountForm,
  setShowCreateAccountForm,
  account,
  onAccountKeyChange,
  showPassword,
  setShowPassword,
  demoOnly,
  demoAccountConfirmed,
  setDemoAccountConfirmed,
}: any) {
  const theme = useTheme()

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {availableAccounts.length > 0 && !showCreateAccountForm && (
        <>
          <FormControl fullWidth>
            <InputLabel>Select Account</InputLabel>
            <Select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              label="Select Account"
              sx={{ backgroundColor: theme.palette.background.custom4 }}
            >
              {availableAccounts.map((acc: any) => (
                <MenuItem key={acc.accountId} value={acc.accountId}>
                  {acc.name} ({acc.login} - {acc.server})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {!showCreateAccountForm && (
        <Button
          variant="outlined"
          color="success"
          onClick={() => setShowCreateAccountForm(true)}
          sx={{ alignSelf: 'flex-start' }}
        >
          + Connect New Account
        </Button>
      )}

      {showCreateAccountForm && (
        <Box>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Connect New Account
            </Typography>
            <Button
              variant="text"
              color="success"
              onClick={() => setShowCreateAccountForm(false)}
              size="small"
            >
              Cancel
            </Button>
          </Box>
          <MetaTraderAccountForm
            account={account}
            onAccountKeyChange={onAccountKeyChange}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />

          {demoOnly && (
            <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2 }}>
              <Checkbox
                checked={demoAccountConfirmed}
                onChange={(e) => setDemoAccountConfirmed(e.target.checked)}
                color="success"
              />
              <Typography variant="body2" color={demoAccountConfirmed ? 'success' : 'warning'}>
                Please confirm that this account is for demo trading only.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

function AccountCreatedConfirmationSlide() {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <CheckIcon sx={{ fontSize: '64px' }} color="success" />
      <Typography variant="h4" fontWeight="bold">
        Account Connected!
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Your MetaTrader account has been successfully connected. You can now enable automated
        trading for your patterns and strategies.
      </Typography>
    </Box>
  )
}

function SetupTradingSlide() {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <TuneIcon sx={{ fontSize: '64px' }} color="success" />
      <Typography variant="h4" fontWeight="bold">
        Setup Trading Parameters
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Configure your trade exit strategy, risk/reward ratios, and other trading parameters to
        enable automated trading for this alert.
      </Typography>
    </Box>
  )
}

function TradingOptionsFormSlide({
  tradingOptions,
  onTradingOptionChange,
  formErrors,
  brokerSettings,
  brokerUserInputs,
  onBrokerUserInputChange,
  exitTradingPlan,
  setExitTradingPlan,
  exitPatternMode,
  setExitPatternMode,
  symbol,
}: {
  tradingOptions: TradingOptions
  onTradingOptionChange: (key: string, value: any) => void
  formErrors?: any
  brokerSettings: any
  brokerUserInputs: any
  onBrokerUserInputChange: (key: string, value: any) => void
  exitTradingPlan: Alert | null
  setExitTradingPlan: (plan: Alert | null) => void
  exitPatternMode: 'both' | 'take_profit_only' | 'stop_loss_only'
  setExitPatternMode: (mode: 'both' | 'take_profit_only' | 'stop_loss_only') => void
  symbol?: string
}) {
  const theme = useTheme()
  const someBrokerSettingsRequired =
    brokerSettings?.required && Object.values(brokerSettings.required).some((value: any) => value)

  const [isExitPatternEnabled, setIsExitPatternEnabled] = useState<boolean>(false)

  useEffect(() => {
    setIsExitPatternEnabled(!!exitTradingPlan)
  }, [exitTradingPlan])

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      sx={{
        '& .MuiInputBase-root, & .MuiSelect-root': {
          backgroundColor: theme.palette.background.custom4,
        },
      }}
    >
      {someBrokerSettingsRequired && (
        <>
          <Typography fontWeight="bold">Broker Details</Typography>
          <Grid container item spacing={2}>
            {brokerSettings?.required?.symbol && (
              <Grid item sm={6} xs={12}>
                <FormControl fullWidth error={formErrors?.broker?.symbol}>
                  <InputLabel htmlFor="outlined-adornment-amount">Symbol Mapping</InputLabel>
                  <OutlinedInput
                    value={brokerUserInputs?.symbol || ''}
                    label="Symbol Mapping"
                    name="symbol"
                    onChange={(e) => onBrokerUserInputChange(e.target.name, e.target.value)}
                    placeholder="Enter symbol"
                    endAdornment={
                      <InputAdornment position="end">
                        <Tooltip title="Brokers may use different naming conventions for the same trading symbol (e.g., 'EURUSD' vs 'EURUSD.pro'). Please enter the exact symbol name as it appears in your broker's platform to ensure correct trade execution.">
                          <InfoIcon sx={{ color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </InputAdornment>
                    }
                  />
                  {formErrors?.broker?.symbol && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formErrors.broker.symbol}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}
          </Grid>
        </>
      )}
      <Typography fontWeight="bold">Account Details</Typography>

      <Grid container item spacing={2}>
        <Grid item sm={6} xs={12}>
          <FormControl fullWidth>
            <InputLabel htmlFor="outlined-adornment-amount">Max risk</InputLabel>
            <OutlinedInput
              value={
                tradingOptions.analyzeTrades.maxRisk
                  ? tradingOptions.analyzeTrades.maxRisk * 100
                  : ''
              }
              placeholder="∞"
              name="analyzeTrades.maxRisk"
              onChange={(e) =>
                // Clearing the field means "unlimited". 0 is the sentinel the
                // sibling limit fields already use, and every read path treats
                // falsy as unlimited. Without the guard parseFloat('') is NaN.
                onTradingOptionChange(e.target.name, parseFloatOr(e.target.value, 0) / 100)
              }
              type="number"
              id="outlined-adornment-amount"
              startAdornment={<InputAdornment position="start">%</InputAdornment>}
              label="Max risk"
              inputProps={{ min: 0 }}
            />
          </FormControl>
        </Grid>

        <Grid item sm={6} xs={12}>
          <FormControl fullWidth>
            <InputLabel htmlFor="outlined-adornment-amount">Risk per trade</InputLabel>
            <OutlinedInput
              value={(tradingOptions.analyzeTrades.riskPerTrade * 100).toFixed(2)}
              name="analyzeTrades.riskPerTrade"
              onChange={(e) => {
                // Required field with a 0.5% minimum and no "unlimited" state,
                // so an empty box is a transient edit rather than a value.
                // Keep the last valid entry instead of writing 0 or NaN.
                const parsed = parseFloat(e.target.value)
                if (Number.isNaN(parsed)) return
                onTradingOptionChange(e.target.name, parsed / 100)
              }}
              type="number"
              inputProps={{ step: 0.5, min: 0.5 }}
              id="outlined-adornment-amount"
              startAdornment={<InputAdornment position="start">%</InputAdornment>}
              label="Risk per trade"
            />
          </FormControl>
        </Grid>

        <Grid item sm={6} xs={12}>
          <FormControl fullWidth>
            <InputLabel htmlFor="outlined-adornment-amount">Max open positions</InputLabel>
            <OutlinedInput
              value={
                tradingOptions.analyzeTrades.tradeLimit === 0
                  ? ''
                  : tradingOptions.analyzeTrades.tradeLimit
              }
              name="analyzeTrades.tradeLimit"
              onChange={(e) => {
                // 0 is the "unlimited" sentinel, and what an empty box means.
                const limit = parseIntOr(e.target.value, 0)
                if (limit === 1) {
                  onTradingOptionChange('analyzeTrades.allowHedging', false)
                }
                onTradingOptionChange(e.target.name, limit)
              }}
              type="number"
              inputProps={{ min: 0 }}
              id="outlined-adornment-amount"
              startAdornment={<InputAdornment position="start">#</InputAdornment>}
              label="Max open positions"
              placeholder="∞"
              endAdornment={
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      <Box className="tooltip-text">
                        {tradingOptions.analyzeTrades.tradeLimit === 1
                          ? 'Change max open positions to enable hedging'
                          : tradingOptions.analyzeTrades.allowHedging
                            ? 'Disable hedging'
                            : 'Allow hedging'}
                      </Box>
                    }
                    arrow
                  >
                    <span>
                      <Switch
                        disabled={tradingOptions.analyzeTrades.tradeLimit === 1}
                        checked={!!tradingOptions.analyzeTrades.allowHedging}
                        onChange={(e) =>
                          onTradingOptionChange('analyzeTrades.allowHedging', e.target.checked)
                        }
                        color="primary"
                      />
                    </span>
                  </Tooltip>
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>

        <Grid item sm={6} xs={12}>
          <FormControl fullWidth>
            <InputLabel htmlFor="outlined-adornment-amount">Daily trade limit</InputLabel>
            <OutlinedInput
              value={
                tradingOptions.analyzeTrades.dailyTradeLimit === 0
                  ? ''
                  : tradingOptions.analyzeTrades.dailyTradeLimit
              }
              placeholder="∞"
              name="analyzeTrades.dailyTradeLimit"
              onChange={(e) => onTradingOptionChange(e.target.name, parseIntOr(e.target.value, 0))}
              type="number"
              id="outlined-adornment-amount"
              startAdornment={<InputAdornment position="start">#</InputAdornment>}
              label="Daily trade limit"
              inputProps={{ min: 0 }}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Typography fontWeight="bold">Stop Loss</Typography>

      <Grid container spacing={2} flexDirection="column">
        <Grid container item spacing={2}>
          <Grid item lg={6} xs={12}>
            <StopLossStrategySelect
              exitStrategy={tradingOptions.generateTrades.exitStrategy}
              onChange={onTradingOptionChange}
            />
          </Grid>
        </Grid>

        <Grid container item spacing={2}>
          <ExitStrategyInputs
            backtestSettingInputData={tradingOptions}
            handleInputChange={onTradingOptionChange}
            formErrors={formErrors}
            symbol={symbol}
          />
        </Grid>
      </Grid>

      <Typography fontWeight="bold">Trade Exit</Typography>

      <Box display="flex" gap={1} alignItems="center" justifyContent="space-between" width="100%">
        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="body1">Pattern Exit</Typography>
          <Typography variant="caption">(Optional)</Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Switch
            checked={isExitPatternEnabled}
            onChange={(e) => {
              const enabled = e.target.checked
              setIsExitPatternEnabled(enabled)
              if (!enabled) setExitTradingPlan(null)
            }}
            color="primary"
          />
        </Box>
      </Box>

      {isExitPatternEnabled && (
        <Grid container item spacing={2}>
          <Grid item sm={6} xs={12}>
            <PatternSelect
              pattern={exitTradingPlan?.pattern}
              onSelect={(pattern: Pattern) => {
                const newExitTradingPlan = {
                  ...exitTradingPlan,
                  pattern: pattern,
                } as Alert
                setExitTradingPlan(newExitTradingPlan)
                setIsExitPatternEnabled(true)
              }}
              helperText="Trades will close when this pattern is met."
            />
          </Grid>
          {exitTradingPlan?.pattern && (
            <Grid item sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>Exit Pattern Mode</InputLabel>
                <Select
                  value={exitPatternMode}
                  onChange={(e) =>
                    setExitPatternMode(
                      e.target.value as 'both' | 'take_profit_only' | 'stop_loss_only'
                    )
                  }
                  label="Exit Pattern Mode"
                >
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="take_profit_only">Take Profit Only</MenuItem>
                  <MenuItem value="stop_loss_only">Stop Loss Only</MenuItem>
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  {exitPatternMode === 'take_profit_only'
                    ? 'Exit pattern is ignored when the trade is at a loss.'
                    : exitPatternMode === 'stop_loss_only'
                      ? 'Exit pattern is ignored when the trade is in profit.'
                      : 'Exit pattern triggers regardless of profit or loss.'}
                </Typography>
              </FormControl>
            </Grid>
          )}
        </Grid>
      )}

      <Grid container spacing={2}>
        <ForceExitField
          analyze={tradingOptions.analyzeTrades}
          updateSetting={onTradingOptionChange}
        />
      </Grid>
    </Box>
  )
}

function TradingSetupCompleteSlide({ isEditing }: { isEditing?: boolean }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <CheckIcon sx={{ fontSize: '64px' }} color="success" />
      <Typography variant="h4" fontWeight="bold">
        {isEditing ? 'Trading Settings Updated!' : 'Trading Setup Complete!'}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {isEditing
          ? 'Your trading account and parameters have been successfully updated for this alert.'
          : 'Your trading parameters have been successfully configured. Automated trading is now enabled for this alert.'}
      </Typography>
    </Box>
  )
}

function getSteps(
  account: MetaTraderAccount,
  selectedAccountId: string,
  showCreateAccountForm: boolean,
  createMetaApiAccount: () => Promise<boolean>,
  updateTradingOptions: () => Promise<boolean>,
  createMetaApiAccountLoading: boolean,
  isUpdatingAlertTradingOptions: boolean,
  errorMessage: string | null,
  isEditing: boolean,
  isTradeRatiosValid: boolean,
  demoOnly: boolean,
  demoAccountConfirmed: boolean,
  isBrokerSettingsValid: boolean
): SlideStep[] {
  const isFormValid =
    account.login && account.password && account.server && (!demoOnly || demoAccountConfirmed)
  const steps: SlideStep[] = []

  // Always start with intro slide
  steps.push({
    slideComponent: SetupAccountSlide,
    actionsComponent: ({ onNextStep }: any) => (
      <Button onClick={onNextStep} variant="contained" color="success">
        {isEditing ? 'Update Settings' : 'Begin Setup'}
      </Button>
    ),
  })

  // Account selection slide
  steps.push({
    slideComponent: AccountSelectionSlide,
    slideName: isEditing ? 'Update Trading Account' : 'Select Trading Account',
    subtitle:
      'Choose which MetaTrader account you want to use for automated trading, or connect a new one.',
    actionsComponent: ({ onNextStep }: any) => (
      <Box display="flex" gap={2} width="100%" height="100%" alignItems="center">
        {createMetaApiAccountLoading && (
          <Typography variant="body2" color="textSecondary" width="100%">
            This could take a few minutes to connect.
          </Typography>
        )}
        {errorMessage && (
          <Typography variant="body2" color="error" width="100%">
            {errorMessage}
          </Typography>
        )}
        {!errorMessage && showCreateAccountForm && !isFormValid && (
          <Typography variant="body2" color="textSecondary" width="100%">
            * Please fill in all required fields to continue.
          </Typography>
        )}
        <Box
          display="flex"
          width="100%"
          height="100%"
          justifyContent="flex-end"
          alignItems="center"
        >
          {showCreateAccountForm ? (
            <Button
              onClick={async () => {
                const success = await createMetaApiAccount()
                if (success) {
                  onNextStep()
                }
              }}
              variant="contained"
              color="success"
              disabled={
                !isFormValid || createMetaApiAccountLoading || (demoOnly && !demoAccountConfirmed)
              }
            >
              {createMetaApiAccountLoading ? 'Connecting...' : 'Connect Account'}
            </Button>
          ) : (
            <Button
              onClick={onNextStep}
              variant="contained"
              color="success"
              disabled={!selectedAccountId}
            >
              Continue
            </Button>
          )}
        </Box>
      </Box>
    ),
  })

  // Show confirmation after account creation if creating new account
  if (showCreateAccountForm) {
    steps.push({
      slideComponent: AccountCreatedConfirmationSlide,
      actionsComponent: ({ onNextStep, onClose }: any) => (
        <Box display="flex" justifyContent="center" width="100%" height="100%">
          <Button onClick={onNextStep} variant="contained" color="success">
            Continue to Trading Setup
          </Button>
        </Box>
      ),
    })
  }

  // Trading parameters setup
  steps.push({
    slideComponent: SetupTradingSlide,
    actionsComponent: SetupTradingActions,
  })

  steps.push({
    slideComponent: TradingOptionsFormSlide,
    actionsComponent: ({ onNextStep }: any) => (
      <Box display="flex" gap={2} width="100%" height="100%" alignItems="center">
        {isUpdatingAlertTradingOptions && (
          <Typography variant="body2" color="textSecondary" width="100%">
            Saving trading parameters...
          </Typography>
        )}
        {errorMessage && (
          <Typography variant="body2" color="error" width="100%">
            {errorMessage}
          </Typography>
        )}
        <Box
          display="flex"
          width="100%"
          height="100%"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            onClick={async () => {
              const success = await updateTradingOptions()
              if (success) onNextStep()
            }}
            variant="contained"
            color="success"
            disabled={
              isUpdatingAlertTradingOptions ||
              !selectedAccountId ||
              !isTradeRatiosValid ||
              !isBrokerSettingsValid
            }
          >
            {isUpdatingAlertTradingOptions
              ? 'Saving...'
              : isEditing
                ? 'Update Trading Settings'
                : 'Save automated trading'}
          </Button>
        </Box>
      </Box>
    ),
  })

  steps.push({
    slideComponent: TradingSetupCompleteSlide,
    actionsComponent: ({ onClose }: any) => (
      <Box display="flex" justifyContent="space-between" width="100%" height="100%">
        <Button onClick={onClose} variant="contained" sx={{ mx: 'auto' }} color="success">
          Done
        </Button>
      </Box>
    ),
  })

  return steps
}

function SetupTradingActions({
  onNextStep,
  getBrokerSettingsLoading,
  existingExitTradingPlanLoading,
}: {
  onNextStep: () => void
  getBrokerSettingsLoading: boolean
  existingExitTradingPlanLoading: boolean
}) {
  const [hasClicked, setHasClicked] = useState(false)

  useEffect(() => {
    if (hasClicked && !getBrokerSettingsLoading && !existingExitTradingPlanLoading) {
      onNextStep()
      setHasClicked(false)
    }
  }, [hasClicked, getBrokerSettingsLoading, existingExitTradingPlanLoading, onNextStep])

  const handleClick = () => {
    setHasClicked(true)
    if (!getBrokerSettingsLoading && !existingExitTradingPlanLoading) {
      onNextStep()
      setHasClicked(false)
    }
  }

  const isLoading = hasClicked && (getBrokerSettingsLoading || existingExitTradingPlanLoading)
  const isConnecting = hasClicked && getBrokerSettingsLoading

  return (
    <Box display="flex" width="100%" height="100%" justifyContent="center" alignItems="center">
      <Button onClick={handleClick} variant="contained" color="success" disabled={isLoading}>
        {isConnecting ? 'Connecting to broker...' : 'Setup Parameters'}
        {isLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
      </Button>
    </Box>
  )
}

export default TradingAccountDialog
